import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { login, logout, getCurrentUser, type LoginCredentials } from '../auth';
import { hasTokens, clearTokens } from '@crm/utils';

/** Query key for current user */
export const currentUserKey = ['auth', 'currentUser'] as const;

/** Hook for current authenticated user */
export function useCurrentUser() {
  return useQuery({
    queryKey: currentUserKey,
    queryFn: getCurrentUser,
    enabled: hasTokens(),
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false,
  });
}

/** Hook for login mutation */
export function useLogin() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (credentials: LoginCredentials) => login(credentials),
    onSuccess: (user) => {
      queryClient.setQueryData(currentUserKey, user);
    },
  });
}

/** Hook for logout mutation */
export function useLogout() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: logout,
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: currentUserKey });
      queryClient.clear();
    },
    onError: () => {
      // Clear tokens even if logout API fails
      clearTokens();
      queryClient.removeQueries({ queryKey: currentUserKey });
      queryClient.clear();
    },
  });
}

/** Check if user is authenticated (has tokens) */
export function useIsAuthenticated(): boolean {
  return hasTokens();
}
